import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAxios } from '@dataplace.ai/functions/utils'
import { AuthContext } from '@dataplace.ai/features'
import { IRange } from '@dataplace.ai/types'
import { AxiosError } from 'axios'
import { TileData } from '@placeme/components/organisms/Tiles/Anw/AnwPotentialModel/@types/IAnwPotentialModelTileData'
import { useAppDispatch } from '../redux/hooks'
import { fetchWorkspaceUsageValue, saveNewRangeAction, saveTileData } from '../features/Analyse/slice/analysisSlice'
import { RootState } from '../redux/store'
import { fetchSubscriptionInfo } from '../features/ChooseLocationReport/chooseLocationSlice'
import { MCD_NO_MALLS_NEAR_ANALYZED_LOCATION_MODAL } from '../constants/errorModals'

interface IUseFetchTileDataOptions {
  regenerateCatchmentOnError?: {
    onAnyError?: boolean
    type: IRange['type']
    value: number
  }
}

const MCD_NO_MALLS_NEAR_ANALYZED_LOCATION_ERROR = 'status.error.mcd.place_not_in_mall'

const getErrorText = (error: unknown): string => {
  const axiosError = error as AxiosError<{
    error?: string
    message?: string
    status?: string
  }>
  const errorMessage = `${axiosError?.message || ''}`.toLowerCase()
  const statusMessage = `${axiosError?.response?.data?.status || ''}`.toLowerCase()
  const apiMessage = `${axiosError?.response?.data?.message || axiosError?.response?.data?.error || ''}`.toLowerCase()

  return `${errorMessage} ${statusMessage} ${apiMessage}`
}

const isCatchmentInUseError = (error: unknown): boolean => {
  const textToCheck = getErrorText(error)

  return textToCheck.includes('catchment.already_in_use')
}

const isMcdNoMallsNearAnalyzedLocationError = (error: unknown): boolean => {
  const textToCheck = getErrorText(error)

  return textToCheck.includes(MCD_NO_MALLS_NEAR_ANALYZED_LOCATION_ERROR)
}

export function useFetchTileData<T, U>(
  data: TileData<T>,
  endpoint: string,
  tileId: string,
  categoryId: string,
  body: U,
  setCanBeRequest?: Dispatch<SetStateAction<boolean>>,
  skipCatchmentId = false,
  options?: IUseFetchTileDataOptions,
): { fetchData: () => Promise<void | null> } {
  const dispatch = useAppDispatch()
  const authContext = useContext(AuthContext)
  const { values } = useSelector((state: RootState) => state.analysis)
  const { currentSubscriptionData } = useSelector((state: RootState) => state.location)
  const [token, setToken] = useState('')
  const isRequestInFlight = useRef(false)

  const fetchData = useCallback(async () => {
    const catchmentId = values.find(c => c.id === categoryId)?.tiles
      ?.find(t => t.id === tileId)?.chosenRange?.catchmentId

    if (!token.length || (!skipCatchmentId && !catchmentId)) return

    const bodyWithCatchmentId = (() => {
      if (!catchmentId) return body
      if (!body || typeof body !== 'object') return body
      // eslint-disable-next-line no-restricted-syntax
      if (!('catchmentId' in body)) return body

      return {
        ...(body as Record<string, unknown>),
        catchmentId,
      } as unknown as U
    })()

    if (isRequestInFlight.current) return

    let saveData = {
      loading: false,
      error: '',
      value: null,
    }
    let requestError: unknown

    isRequestInFlight.current = true

    try {
      const axiosInstance = await getAxios({
        errCallbackFn: (e) => {
          requestError = e
          saveData = {
            loading: false,
            error: e.message,
            value: null,
          }
        },
      })
      const response = await axiosInstance.post(endpoint, bodyWithCatchmentId)
      if (!response) {
        if (isMcdNoMallsNearAnalyzedLocationError(requestError)) {
          window?.localStorage.setItem(MCD_NO_MALLS_NEAR_ANALYZED_LOCATION_MODAL, catchmentId || 'no catchment')
          window?.dispatchEvent(new CustomEvent(MCD_NO_MALLS_NEAR_ANALYZED_LOCATION_MODAL))

          return setCanBeRequest ? setCanBeRequest(false) : null
        }

        const shouldRegenerateFromAnyError = !!options?.regenerateCatchmentOnError?.onAnyError
        const shouldRegenerateCatchment = !!catchmentId
          && !!options?.regenerateCatchmentOnError
          && (
            shouldRegenerateFromAnyError
            || isCatchmentInUseError(requestError)
          )

        if (shouldRegenerateCatchment && options?.regenerateCatchmentOnError) {
          const {
            type,
            value,
          } = options.regenerateCatchmentOnError

          dispatch(saveNewRangeAction(
            token,
            authContext.userData.user?.uid || '',
            categoryId,
            tileId,
            {
              id: `${tileId}-${value}-${type}`,
              type,
              value,
            },
          ))
        }

        return setCanBeRequest ? setCanBeRequest(false) : null
      }
      saveData = {
        loading: false,
        error: '',
        value: response.data,
      }

      if (response.status === 204) {
        window?.localStorage.setItem('noDataModal', catchmentId || 'no catchment')
        window?.dispatchEvent(new CustomEvent('noDataModal'))
      }

      if (saveData.value) {
        dispatch(saveTileData(categoryId, tileId, saveData))
        dispatch(fetchSubscriptionInfo(currentSubscriptionData?.value?.subscriptionId || ''))
      }
    } finally {
      isRequestInFlight.current = false
    }
  }, [token, values, body])

  useEffect(() => {
    if (token.length && !data?.mapLocation) {
      dispatch(fetchWorkspaceUsageValue())
    }
  }, [token, data])

  useEffect(() => {
    authContext?.userData.user?.getIdToken()?.then((response) => {
      setToken(response)
    })
  }, [authContext])

  return {
    fetchData,
  }
}
